import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationSummaryDto } from 'korint-sdk';
import { Button } from '@folio/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@folio/components/ui/popover';
import { ChevronDownIcon, ChevronsRightIcon, MessageSquarePlusIcon } from 'lucide-react';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type DateGroup = {
  label: string;
  conversations: ConversationSummaryDto[];
};

function groupByDate(
  conversations: ConversationSummaryDto[],
  labels: Record<string, string>,
): DateGroup[] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000);
  const startOf7Days = new Date(startOfToday.getTime() - 7 * 86_400_000);
  const startOf30Days = new Date(startOfToday.getTime() - 30 * 86_400_000);

  const groups: Record<string, ConversationSummaryDto[]> = {
    today: [],
    yesterday: [],
    last7: [],
    last30: [],
    older: [],
  };

  for (const conv of conversations) {
    const d = new Date(conv.updatedAt);
    if (d >= startOfToday) groups.today.push(conv);
    else if (d >= startOfYesterday) groups.yesterday.push(conv);
    else if (d >= startOf7Days) groups.last7.push(conv);
    else if (d >= startOf30Days) groups.last30.push(conv);
    else groups.older.push(conv);
  }

  const order: [string, string][] = [
    ['today', labels.today],
    ['yesterday', labels.yesterday],
    ['last7', labels.last7],
    ['last30', labels.last30],
    ['older', labels.older],
  ];

  return order
    .filter(([key]) => groups[key].length > 0)
    .map(([key, label]) => ({ label, conversations: groups[key] }));
}

export function FloatingChatHeader({
  currentTitle,
  conversations,
  onSelectConversation,
  onNewConversation,
  onClose,
}: {
  currentTitle: string | null;
  conversations: ConversationSummaryDto[];
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const dateLabels = {
    today: t('conversation.dateToday'),
    yesterday: t('conversation.dateYesterday'),
    last7: t('conversation.dateLast7Days'),
    last30: t('conversation.dateLast30Days'),
    older: t('conversation.dateOlder'),
  };

  const groups = useMemo(() => groupByDate(conversations, dateLabels), [conversations, t]);

  return (
    <Group justify="between" gap={2} className="border-b px-4 py-3">
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 font-medium min-w-0 max-w-full">
            <Text inline truncate>
              {currentTitle ?? t('conversation.newConversation')}
            </Text>
            <ChevronDownIcon className="h-4 w-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <div className="max-h-60 overflow-y-auto">
            {groups.map(group => (
              <div key={group.label}>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {group.label}
                </div>
                {group.conversations.map(conv => (
                  <button
                    key={conv.id}
                    type="button"
                    className="w-full text-left rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors truncate"
                    onClick={() => {
                      onSelectConversation(conv.id);
                      setPopoverOpen(false);
                    }}
                  >
                    {conv.title ?? t('conversation.newConversation')}
                  </button>
                ))}
              </div>
            ))}
            {conversations.length === 0 && (
              <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                {t('conversation.noConversations')}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Group gap={1}>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={t('conversation.newConversation')}
          onClick={() => {
            onNewConversation();
            setPopoverOpen(false);
          }}
        >
          <MessageSquarePlusIcon aria-hidden="true" className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={t('conversation.closeChat')}
          onClick={onClose}
        >
          <ChevronsRightIcon aria-hidden="true" className="h-4 w-4" />
        </Button>
      </Group>
    </Group>
  );
}
